export interface DayContext {
  launch_date: string;
  shutdown_date: string;
  not_change_flag: boolean;
}

export interface Drive<TText, TValue> {
  date: TText;
  carry_volume: TValue;
  combustible_waste_carry_volume: TValue;
  pruning_branches_carry_volume: TValue;
  small_animals_carry_volume: TValue;
  combustible_bulk_waste_carry_volume: TValue;
  shredded_waste_residue_carry_volume: TValue;
  disaster_waste_internal_carry_volume: TValue;
  disaster_waste_external_carry_volume: TValue;
  other_carry_volume: TValue;
  incineration_facility_bottom_ash_carry_volume: TValue;
  recyclables_carry_volume: TValue;
  burn_volume: TValue[];
  burn_load: TValue[];
  operating_days: TValue[];
  furnace_operating_days: TValue[];
  electric_create: TValue;
  electric_use: TValue;
  plant_power_high_voltage_main_energy: TValue;
  building_power_high_voltage_main_energy: TValue;
  lighting_power_high_voltage_main_energy: TValue;
  security_power_high_voltage_main_energy: TValue;
  recycle_emergency_low_voltage_power_energy: TValue;
  electric_sale: TValue;
  electric_buy: TValue;
  hot_water_supply_heating_value: TValue;
  main_ash_generate: TValue;
  main_ash_carry_out: TValue;
  main_ash_carry_out_units: TValue;
  fly_ash_generate: TValue;
  fly_ash_carry_out: TValue;
  dry_fly_ash_carry_out: TValue;
  processed_fly_ash_carry_out: TValue;
  fly_ash_carry_out_units: TValue;
  trash_pit_rest: TValue;
  main_ash_pit_rest: TValue;
  fly_ash_pit_rest: TValue;
  origin_electric_create: TValue;
  waste_lower_heating_value: TValue[];
}

export type DriveContext = Drive<string, number>;
export type DriveEmpty = Drive<boolean, boolean>;
export type DriveUsage = Pick<
  Drive<boolean, boolean>,
  | 'carry_volume'
  | 'combustible_waste_carry_volume'
  | 'pruning_branches_carry_volume'
  | 'small_animals_carry_volume'
  | 'combustible_bulk_waste_carry_volume'
  | 'shredded_waste_residue_carry_volume'
  | 'disaster_waste_internal_carry_volume'
  | 'disaster_waste_external_carry_volume'
  | 'other_carry_volume'
  | 'incineration_facility_bottom_ash_carry_volume'
  | 'recyclables_carry_volume'
  | 'electric_create'
  | 'electric_use'
  | 'plant_power_high_voltage_main_energy'
  | 'building_power_high_voltage_main_energy'
  | 'lighting_power_high_voltage_main_energy'
  | 'security_power_high_voltage_main_energy'
  | 'recycle_emergency_low_voltage_power_energy'
  | 'electric_sale'
  | 'electric_buy'
  | 'hot_water_supply_heating_value'
  | 'main_ash_generate'
  | 'fly_ash_generate'
  | 'main_ash_carry_out'
  | 'fly_ash_carry_out'
  | 'dry_fly_ash_carry_out'
  | 'processed_fly_ash_carry_out'
  | 'main_ash_carry_out_units'
  | 'fly_ash_carry_out_units'
  | 'main_ash_pit_rest'
  | 'fly_ash_pit_rest'
> & {
  total_carry_volume: boolean;
  total_electric_use: boolean;
  total_fly_ash_carry_out: boolean;
  total_carry_out: boolean;
  furnace_operating_days: boolean;
  burn_load: boolean;
  burn_load_include_shutdown: boolean;
  waste_lower_heating_value: boolean;
};

export interface Monitor<TValue> {
  total_carry_volume: TValue;
  main_ash_carry_out_sum: TValue;
  fly_ash_carry_out_sum: TValue;
  electric_use_sum: TValue;
}

export type MonitorContext = Monitor<number>;

export class MonitorContextObject implements MonitorContext {
  total_carry_volume: number;
  main_ash_carry_out_sum: number;
  fly_ash_carry_out_sum: number;
  electric_use_sum: number;

  constructor(value: number, furnacesCount: number) {
    this.total_carry_volume = value;
    this.main_ash_carry_out_sum = value;
    this.fly_ash_carry_out_sum = value;
    this.electric_use_sum = value;
  }
}

export interface MonitorMonth<TValue> {
  burn_load: TValue[];
  burn_load_include_shutdown: TValue[];
  burn_load_sum: TValue;
  burn_load_include_shutdown_sum: TValue;
  operating_days: TValue[];
  furnace_operating_days: TValue[];
}

export type MonitorMonthContext = MonitorMonth<number>;

export class MonitorMonthContextObject implements MonitorMonthContext {
  burn_load: number[] = [] as number[];
  burn_load_include_shutdown: number[] = [] as number[];
  burn_load_sum: number;
  burn_load_include_shutdown_sum: number;
  operating_days: number[] = [] as number[];
  furnace_operating_days: number[] = [] as number[];

  constructor(value: number, furnacesCount: number) {
    this.burn_load = Array(furnacesCount).fill(value);
    this.burn_load_include_shutdown = Array(furnacesCount).fill(value);
    this.burn_load_sum = value;
    this.burn_load_include_shutdown_sum = value;
    this.operating_days = Array(furnacesCount).fill(value);
    this.furnace_operating_days = Array(furnacesCount + 1).fill(value);
  }
}

export interface DisplayVals {
  carry_volume: string[];
  burn_volume: string[][];
  trash_pit: string[];
}

export interface MainContext {
  active_year: string;
  year_list: string[];
}

export class MainContextObject implements MainContext {
  active_year: string = '';
  year_list: string[] = [] as string[];
}

export class DayContextObject implements DayContext {
  launch_date: string = '';
  shutdown_date: string = '';
  not_change_flag: boolean = false;
}

export interface Draft<TDrive> {
  day: TDrive[];
  month: TDrive[];
  shutdown: DayContext[][];
}

export type DraftContext = Draft<DriveContext>;
export type DraftEmpty = Omit<Draft<DriveEmpty>, 'shutdown'>;

export class DraftContextObject implements DraftContext {
  day: DriveContext[];
  month: DriveContext[];
  shutdown: DayContext[][];
  constructor(value: number, furnacesCount: number) {
    this.day = Array.from({ length: 366 }, (_, index) => new DriveContextObject(value, furnacesCount));
    this.month = Array.from({ length: 12 }, (_, index) => new DriveContextObject(value, furnacesCount));
    this.shutdown = [[new DayContextObject()]];
  }
}

export class DraftEmptyObject implements DraftEmpty {
  day: DriveEmpty[];
  month: DriveEmpty[];
  constructor(value: boolean, furnacesCount: number) {
    this.day = Array.from({ length: 366 }, (_, index) => new DriveEmptyObject(value, furnacesCount));
    this.month = Array.from({ length: 12 }, (_, index) => new DriveEmptyObject(value, furnacesCount));
  }
}

export interface ConditionContext {
  ashPitCapacity: number;
  rateCapacity: number;
  weeklyCarryCalculation: number[];
  mainAshUnit: number;
  flyAshUnit: number;
  pitLimit: number;
  pitMin: number;
  pitMax: number;
  trashDensity: number;
  burnloadCapacity: number[];
  electricCreate: number[][];
  electricUse: number[][];
  mainAshRate: number;
  flyAshRate: number;
  truckMainAshCarryOut: number;
  truckFlyAshCarryOut: number;
  shutdownLaunchHour: number[];
  shutdownBurnHours: number[];
  shutdownElectricCreateHours: number[];
}

export class ConditionContextObject implements ConditionContext {
  ashPitCapacity: number = 1;
  furnacesCount: number = 0;
  rateCapacity: number = 0;
  weeklyCarryCalculation: number[] = [] as number[];
  mainAshUnit: number = 0;
  flyAshUnit: number = 0;
  pitLimit: number = 0;
  pitMin: number = 0;
  pitMax: number = 0;
  trashDensity: number = 1;
  burnloadCapacity: number[] = [] as number[];
  electricCreate: number[][] = [] as number[][];
  electricUse: number[][] = [] as number[][];
  mainAshRate: number = 0;
  flyAshRate: number = 0;
  truckMainAshCarryOut: number = 0;
  truckFlyAshCarryOut: number = 0;
  shutdownLaunchHour: number[] = [] as number[];
  shutdownBurnHours: number[] = [] as number[];
  shutdownElectricCreateHours: number[] = [] as number[];
}

export interface SessionContext {
  branchId: number;
  furnacesCount: number;
  selectYear: number;
}

export class DriveContextObject implements DriveContext {
  date: string;
  carry_volume: number;
  combustible_waste_carry_volume: number;
  pruning_branches_carry_volume: number;
  small_animals_carry_volume: number;
  combustible_bulk_waste_carry_volume: number;
  shredded_waste_residue_carry_volume: number;
  disaster_waste_internal_carry_volume: number;
  disaster_waste_external_carry_volume: number;
  other_carry_volume: number;
  incineration_facility_bottom_ash_carry_volume: number;
  recyclables_carry_volume: number;
  burn_volume: number[];
  burn_load: number[];
  operating_days: number[];
  furnace_operating_days: number[];
  electric_create: number;
  electric_use: number;
  plant_power_high_voltage_main_energy: number;
  building_power_high_voltage_main_energy: number;
  lighting_power_high_voltage_main_energy: number;
  security_power_high_voltage_main_energy: number;
  recycle_emergency_low_voltage_power_energy: number;
  electric_sale: number;
  electric_buy: number;
  hot_water_supply_heating_value: number;
  main_ash_generate: number;
  main_ash_carry_out: number;
  main_ash_carry_out_units: number;
  fly_ash_generate: number;
  fly_ash_carry_out: number;
  dry_fly_ash_carry_out: number;
  processed_fly_ash_carry_out: number;
  fly_ash_carry_out_units: number;
  trash_pit_rest: number;
  main_ash_pit_rest: number;
  fly_ash_pit_rest: number;
  origin_electric_create: number;
  waste_lower_heating_value: number[];

  constructor(value: number, furnacesCount: number) {
    this.date = '';
    this.carry_volume = value;
    this.combustible_waste_carry_volume = value;
    this.pruning_branches_carry_volume = value;
    this.small_animals_carry_volume = value;
    this.combustible_bulk_waste_carry_volume = value;
    this.shredded_waste_residue_carry_volume = value;
    this.disaster_waste_internal_carry_volume = value;
    this.disaster_waste_external_carry_volume = value;
    this.other_carry_volume = value;
    this.incineration_facility_bottom_ash_carry_volume = value;
    this.recyclables_carry_volume = value;
    this.burn_volume = Array(furnacesCount).fill(value);
    this.burn_load = Array(furnacesCount).fill(value);
    this.operating_days = Array(furnacesCount + 1).fill(value);
    this.furnace_operating_days = Array(furnacesCount).fill(value);
    this.electric_create = value;
    this.electric_use = value;
    this.plant_power_high_voltage_main_energy = value;
    this.building_power_high_voltage_main_energy = value;
    this.lighting_power_high_voltage_main_energy = value;
    this.security_power_high_voltage_main_energy = value;
    this.recycle_emergency_low_voltage_power_energy = value;
    this.electric_sale = value;
    this.electric_buy = value;
    this.hot_water_supply_heating_value = value;
    this.main_ash_generate = value;
    this.main_ash_carry_out = value;
    this.main_ash_carry_out_units = value;
    this.fly_ash_generate = value;
    this.fly_ash_carry_out = value;
    this.dry_fly_ash_carry_out = value;
    this.processed_fly_ash_carry_out = value;
    this.fly_ash_carry_out_units = value;
    this.trash_pit_rest = value;
    this.main_ash_pit_rest = value;
    this.fly_ash_pit_rest = value;
    this.origin_electric_create = value;
    this.waste_lower_heating_value = Array(furnacesCount).fill(value);
  }
}

export class DriveEmptyObject implements DriveEmpty {
  date: boolean;
  carry_volume: boolean;
  combustible_waste_carry_volume: boolean;
  pruning_branches_carry_volume: boolean;
  small_animals_carry_volume: boolean;
  combustible_bulk_waste_carry_volume: boolean;
  shredded_waste_residue_carry_volume: boolean;
  disaster_waste_internal_carry_volume: boolean;
  disaster_waste_external_carry_volume: boolean;
  other_carry_volume: boolean;
  incineration_facility_bottom_ash_carry_volume: boolean;
  recyclables_carry_volume: boolean;
  burn_volume: boolean[];
  burn_load: boolean[];
  operating_days: boolean[];
  furnace_operating_days: boolean[];
  electric_create: boolean;
  electric_use: boolean;
  plant_power_high_voltage_main_energy: boolean;
  building_power_high_voltage_main_energy: boolean;
  lighting_power_high_voltage_main_energy: boolean;
  security_power_high_voltage_main_energy: boolean;
  recycle_emergency_low_voltage_power_energy: boolean;
  electric_sale: boolean;
  electric_buy: boolean;
  hot_water_supply_heating_value: boolean;
  main_ash_generate: boolean;
  main_ash_carry_out: boolean;
  main_ash_carry_out_units: boolean;
  fly_ash_generate: boolean;
  fly_ash_carry_out: boolean;
  dry_fly_ash_carry_out: boolean;
  processed_fly_ash_carry_out: boolean;
  fly_ash_carry_out_units: boolean;
  trash_pit_rest: boolean;
  main_ash_pit_rest: boolean;
  fly_ash_pit_rest: boolean;
  origin_electric_create: boolean;
  waste_lower_heating_value: boolean[];

  constructor(value: boolean, furnacesCount: number) {
    this.date = value;
    this.carry_volume = value;
    this.combustible_waste_carry_volume = value;
    this.pruning_branches_carry_volume = value;
    this.small_animals_carry_volume = value;
    this.combustible_bulk_waste_carry_volume = value;
    this.shredded_waste_residue_carry_volume = value;
    this.disaster_waste_internal_carry_volume = value;
    this.disaster_waste_external_carry_volume = value;
    this.other_carry_volume = value;
    this.incineration_facility_bottom_ash_carry_volume = value;
    this.recyclables_carry_volume = value;
    this.burn_volume = Array(furnacesCount).fill(value);
    this.burn_load = Array(furnacesCount).fill(value);
    this.operating_days = Array(furnacesCount).fill(value);
    this.furnace_operating_days = Array(furnacesCount + 1).fill(value);
    this.electric_create = value;
    this.electric_use = value;
    this.plant_power_high_voltage_main_energy = value;
    this.building_power_high_voltage_main_energy = value;
    this.lighting_power_high_voltage_main_energy = value;
    this.security_power_high_voltage_main_energy = value;
    this.recycle_emergency_low_voltage_power_energy = value;
    this.electric_sale = value;
    this.electric_buy = value;
    this.hot_water_supply_heating_value = value;
    this.main_ash_generate = value;
    this.main_ash_carry_out = value;
    this.main_ash_carry_out_units = value;
    this.fly_ash_generate = value;
    this.fly_ash_carry_out = value;
    this.dry_fly_ash_carry_out = value;
    this.processed_fly_ash_carry_out = value;
    this.fly_ash_carry_out_units = value;
    this.trash_pit_rest = value;
    this.main_ash_pit_rest = value;
    this.fly_ash_pit_rest = value;
    this.origin_electric_create = value;
    this.waste_lower_heating_value = Array(furnacesCount).fill(value);
  }
}

export class DriveUsageObject implements DriveUsage {
  carry_volume: boolean;
  combustible_waste_carry_volume: boolean;
  pruning_branches_carry_volume: boolean;
  small_animals_carry_volume: boolean;
  combustible_bulk_waste_carry_volume: boolean;
  shredded_waste_residue_carry_volume: boolean;
  disaster_waste_internal_carry_volume: boolean;
  disaster_waste_external_carry_volume: boolean;
  other_carry_volume: boolean;
  incineration_facility_bottom_ash_carry_volume: boolean;
  recyclables_carry_volume: boolean;
  electric_create: boolean;
  electric_use: boolean;
  plant_power_high_voltage_main_energy: boolean;
  building_power_high_voltage_main_energy: boolean;
  lighting_power_high_voltage_main_energy: boolean;
  security_power_high_voltage_main_energy: boolean;
  recycle_emergency_low_voltage_power_energy: boolean;
  electric_sale: boolean;
  electric_buy: boolean;
  hot_water_supply_heating_value: boolean;
  main_ash_generate: boolean;
  fly_ash_generate: boolean;
  main_ash_carry_out: boolean;
  fly_ash_carry_out: boolean;
  dry_fly_ash_carry_out: boolean;
  processed_fly_ash_carry_out: boolean;
  main_ash_carry_out_units: boolean;
  fly_ash_carry_out_units: boolean;
  main_ash_pit_rest: boolean;
  fly_ash_pit_rest: boolean;
  total_carry_volume: boolean;
  total_electric_use: boolean;
  total_fly_ash_carry_out: boolean;
  total_carry_out: boolean;
  furnace_operating_days: boolean;
  burn_load: boolean;
  burn_load_include_shutdown: boolean;
  waste_lower_heating_value: boolean;

  constructor(value: boolean, furnacesCount: number) {
    this.carry_volume = value;
    this.combustible_waste_carry_volume = value;
    this.pruning_branches_carry_volume = value;
    this.small_animals_carry_volume = value;
    this.combustible_bulk_waste_carry_volume = value;
    this.shredded_waste_residue_carry_volume = value;
    this.disaster_waste_internal_carry_volume = value;
    this.disaster_waste_external_carry_volume = value;
    this.other_carry_volume = value;
    this.incineration_facility_bottom_ash_carry_volume = value;
    this.recyclables_carry_volume = value;
    this.electric_create = value;
    this.electric_use = value;
    this.plant_power_high_voltage_main_energy = value;
    this.building_power_high_voltage_main_energy = value;
    this.lighting_power_high_voltage_main_energy = value;
    this.security_power_high_voltage_main_energy = value;
    this.recycle_emergency_low_voltage_power_energy = value;
    this.electric_sale = value;
    this.electric_buy = value;
    this.hot_water_supply_heating_value = value;
    this.main_ash_generate = value;
    this.fly_ash_generate = value;
    this.main_ash_carry_out = value;
    this.fly_ash_carry_out = value;
    this.dry_fly_ash_carry_out = value;
    this.processed_fly_ash_carry_out = value;
    this.main_ash_carry_out_units = value;
    this.fly_ash_carry_out_units = value;
    this.main_ash_pit_rest = value;
    this.fly_ash_pit_rest = value;
    this.total_carry_volume = value;
    this.total_electric_use = value;
    this.total_fly_ash_carry_out = value;
    this.total_carry_out = value;
    this.furnace_operating_days = value;
    this.burn_load = value;
    this.burn_load_include_shutdown = value;
    this.waste_lower_heating_value = value;
  }
}

export const DraftContextObjectIndexMap = Object.keys(new DraftContextObject(0, 3)).reduce((map, key, index) => {
  map[key] = index;
  return map;
}, {} as { [key: string]: number });

export const DriveContextObjectIndexMap = Object.keys(new DriveContextObject(0, 3)).reduce((map, key, index) => {
  map[key] = index;
  return map;
}, {} as { [key: string]: number });

export const MonitorObjectIndexMap = Object.keys(new MonitorContextObject(0, 3)).reduce((map, key, index) => {
  map[key] = index;
  return map;
}, {} as { [key: string]: number });

export const MonitorMonthObjectIndexMap = Object.keys(new MonitorMonthContextObject(0, 3)).reduce((map, key, index) => {
  map[key] = index;
  return map;
}, {} as { [key: string]: number });
